import { db } from './firebase';
import { collection, addDoc, query, where, orderBy, limit, getDocs, getDoc, doc } from 'firebase/firestore';
import { isPaidSongRequestForLedger } from '@/lib/payoutLedger';
import { getSongInteractionsForSession, getArtistRequestsForSession, pageVisitBelongsToSessionFunnel } from '@/lib/sessionFunnelQueries';

const deriveTipMetrics = (requests = [], tips = []) => {
  const requestTips = requests
    .filter((req) => (Number(req.tip) || 0) > 0)
    .map((req) => ({
      id: req.id || `req_tip_${req.song_id || req.song_title || 'unknown'}_${req.created_at?.seconds || Date.now()}`,
      amount: Number(req.tip) || 0,
      requester_name: req.requester_name || req.from_name || 'Anonymous',
      note: req.note || '',
      created_at: req.created_at || req.timestamp || null,
      source: 'request',
    }));

  const collectionTips = (tips || []).map((tip) => ({
    id: tip.id,
    amount: Number(tip.amount) || 0,
    requester_name: tip.requester_name || tip.from_name || 'Anonymous',
    note: tip.note || tip.message || '',
    created_at: tip.created_at || tip.timestamp || null,
    source: 'tips_collection',
  }));

  const requestTipTotal = requestTips.reduce((sum, tip) => sum + (Number(tip.amount) || 0), 0);
  const collectionTipTotal = collectionTips.reduce((sum, tip) => sum + (Number(tip.amount) || 0), 0);

  return {
    totalTips: Math.max(requestTipTotal, collectionTipTotal),
    tipsForStorage: collectionTips.length > 0 ? collectionTips : requestTips,
  };
};

/**
 * Store a completed live session in the session history with comprehensive analytics
 * @param {Object} sessionData - The session data to store
 * @param {string} sessionData.userId - User ID
 * @param {string} sessionData.sessionId - Session ID
 * @param {Date} sessionData.startTime - Session start time
 * @param {Date} sessionData.endTime - Session end time
 * @param {number} sessionData.durationMinutes - Session duration in minutes
 * @param {number} sessionData.totalRequests - Total requests received
 * @param {number} sessionData.playedRequests - Requests that were played
 * @param {number} sessionData.totalEarnings - Total earnings from requests
 * @param {number} sessionData.totalTips - Total tips received
 * @param {Array} sessionData.requests - Array of request objects
 * @param {Array} sessionData.tips - Array of tip objects
 * @param {number} sessionData.viewerCount - Current viewer count
 * @param {number} sessionData.peakViewers - Peak viewer count
 * @param {string} sessionData.venue - Venue name
 * @param {number} sessionData.uniqueVisitors - Unique visitors count
 * @param {number} sessionData.totalViews - Total page views
 * @param {number} sessionData.songsAddedToCart - Songs added to cart
 * @param {number} sessionData.checkouts - Number of checkouts
 * @param {number} sessionData.conversionRate - Conversion rate percentage
 */
export const storeSessionHistory = async (sessionData) => {
  try {
    console.log('🔍 DEBUG: storeSessionHistory called with:', sessionData);
    
    // Use Firestore Timestamp for proper date handling
    const { Timestamp } = await import('firebase/firestore');
    
    const cart = Number(sessionData.songsAddedToCart) || 0;
    const paidOut = Number(sessionData.checkouts) || 0;
    const conversionRate =
      sessionData.conversionRate !== undefined && sessionData.conversionRate !== null
        ? parseFloat(sessionData.conversionRate)
        : cart > 0
          ? parseFloat(((paidOut / cart) * 100).toFixed(2))
          : paidOut > 0
            ? 100
            : 0;
    
    const sessionHistoryData = {
      user_id: sessionData.userId,
      session_id: sessionData.sessionId,
      start_time: sessionData.startTime instanceof Date 
        ? Timestamp.fromDate(sessionData.startTime)
        : (sessionData.startTime?.toDate ? sessionData.startTime.toDate() : new Date(sessionData.startTime)),
      end_time: sessionData.endTime instanceof Date
        ? Timestamp.fromDate(sessionData.endTime)
        : (sessionData.endTime?.toDate ? sessionData.endTime.toDate() : new Date(sessionData.endTime)),
      duration_minutes: sessionData.durationMinutes || 0,
      total_requests: sessionData.totalRequests || 0,
      played_requests: sessionData.playedRequests || 0,
      total_earnings: sessionData.totalEarnings || 0,
      total_tips: sessionData.totalTips || 0,
      viewer_count: sessionData.viewerCount || sessionData.peakViewers || 0,
      peak_viewers: sessionData.peakViewers || sessionData.viewerCount || 0,
      unique_visitors: sessionData.uniqueVisitors || 0,
      total_views: sessionData.totalViews || 0,
      songs_added_to_cart: sessionData.songsAddedToCart || 0,
      checkouts: sessionData.checkouts || 0,
      conversion_rate: parseFloat(conversionRate) || 0,
      venue: sessionData.venue || '',
      requests: sessionData.requests || [],
      tips: sessionData.tips || [],
      created_at: Timestamp.now(),
    };

    ['karaoke_free_requests', 'karaoke_paid_cart', 'karaoke_paid_checkouts', 'session_mode'].forEach((k) => {
      if (sessionData[k] !== undefined && sessionData[k] !== null) {
        sessionHistoryData[k] = sessionData[k];
      }
    });

    // Ensure dates are Timestamps
    if (sessionHistoryData.start_time instanceof Date) {
      sessionHistoryData.start_time = Timestamp.fromDate(sessionHistoryData.start_time);
    }
    if (sessionHistoryData.end_time instanceof Date) {
      sessionHistoryData.end_time = Timestamp.fromDate(sessionHistoryData.end_time);
    }

    console.log('🔍 DEBUG: Session history data to store:', sessionHistoryData);
    
    const docRef = await addDoc(collection(db, 'session_history'), sessionHistoryData);
    console.log('✅ Session history stored successfully with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('❌ Error storing session history:', error);
    throw error; // Re-throw so caller can handle
  }
};

/**
 * Automatically save session history when a session ends
 * Fetches all relevant data and calculates comprehensive analytics
 */
export const saveSessionOnEnd = async (userId, sessionId, startTime, endTime, venue = '') => {
  try {
    console.log('💾 Saving session on end:', { userId, sessionId, startTime, endTime });
    
    const { Timestamp } = await import('firebase/firestore');
    
    // Convert times to Date objects if needed
    const start = startTime?.toDate ? startTime.toDate() : new Date(startTime);
    const end = endTime?.toDate ? endTime.toDate() : new Date(endTime);
    const durationMinutes = Math.round((end - start) / (1000 * 60));
    
    const requests = await getArtistRequestsForSession(userId, sessionId);
    
    // Fetch all tips for this session
    const tipsQuery = query(
      collection(db, 'tips'),
      where('artist_id', '==', userId),
      where('session_id', '==', sessionId)
    );
    const tipsSnapshot = await getDocs(tipsQuery);
    const tips = tipsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // Calculate request stats
    const totalRequests = requests.length;
    const playedRequests = requests.filter(
      (req) => req.status === 'played' || req.played === true
    ).length;
    const totalEarnings = requests
      .filter((req) => isPaidSongRequestForLedger(req))
      .reduce((sum, req) => sum + (Number(req.amount_paid) || 0), 0);
    const { totalTips, tipsForStorage } = deriveTipMetrics(requests, tips);

    let uniqueVisitors = 0;
    let totalViews = 0;
    let songsAddedToCart = 0;

    try {
      const visitsQuery = query(
        collection(db, 'page_visits'),
        where('artist_id', '==', userId)
      );
      const visitsSnapshot = await getDocs(visitsQuery);
      const sessionVisits = visitsSnapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((visit) => pageVisitBelongsToSessionFunnel(visit, sessionId, start, end));

      uniqueVisitors = new Set(sessionVisits.map((v) => v.visitor_id)).size;
      totalViews = sessionVisits.length;

      const interactions = await getSongInteractionsForSession(userId, sessionId, start, end);
      songsAddedToCart = interactions.filter((i) => i.interaction_type === 'added_to_cart').length;
    } catch (analyticsErr) {
      console.warn('Session funnel analytics skipped (permissions or index):', analyticsErr?.message || analyticsErr);
    }

    const paidCheckoutCount = requests.filter((req) => isPaidSongRequestForLedger(req)).length;
    const checkouts = paidCheckoutCount;
    
    // Get peak viewers from live_status if available
    let peakViewers = 0;
    let viewerCount = 0;
    try {
      const liveStatusDoc = await getDoc(doc(db, 'live_status', userId));
      if (liveStatusDoc.exists()) {
        const liveData = liveStatusDoc.data();
        peakViewers = liveData.peak_viewers || liveData.viewer_count || 0;
        viewerCount = liveData.viewer_count || 0;
      }
    } catch (err) {
      console.warn('Could not fetch viewer count from live_status:', err);
    }
    
    const conversionRate =
      songsAddedToCart > 0
        ? parseFloat(((checkouts / songsAddedToCart) * 100).toFixed(2))
        : checkouts > 0
          ? 100
          : 0;
    
    // Store comprehensive session history
    const sessionData = {
      userId,
      sessionId,
      startTime: start,
      endTime: end,
      durationMinutes,
      totalRequests,
      playedRequests,
      totalEarnings,
      totalTips,
      viewerCount,
      peakViewers,
      uniqueVisitors,
      totalViews,
      songsAddedToCart,
      checkouts,
      conversionRate: parseFloat(conversionRate),
      venue,
      requests,
      tips: tipsForStorage,
      session_mode: 'artist',
    };
    
    return await storeSessionHistory(sessionData);
  } catch (error) {
    console.error('❌ Error saving session on end:', error);
    throw error;
  }
};

/**
 * Get recent session history for a user
 * @param {string} userId - User ID
 * @param {number} limitCount - Number of sessions to retrieve (default: 5)
 * @returns {Array} Array of session history objects
 */
export const getSessionHistory = async (userId, limitCount = 5) => {
  try {
    console.log('🔍 DEBUG: getSessionHistory called for user:', userId);
    
    const q = query(
      collection(db, 'session_history'),
      where('user_id', '==', userId),
      orderBy('end_time', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const sessions = querySnapshot.docs.map(doc => {
      const data = doc.data();
      const requestTipTotal = (data.requests || []).reduce((sum, req) => {
        const directTip = Number(req.tip) || 0;
        if (directTip > 0) return sum + directTip;
        if (req.is_tip_only) return sum + (Number(req.amount_paid) || 0);
        return sum;
      }, 0);

      return {
        id: doc.id,
        ...data,
        total_tips: Math.max(Number(data.total_tips) || 0, requestTipTotal),
      };
    });

    console.log('🔍 DEBUG: Found sessions:', sessions.length, sessions);
    return sessions;
  } catch (error) {
    console.error('❌ Error fetching session history:', error);
    return [];
  }
};

/**
 * Calculate session summary from current session data
 * @param {Array} requests - Current session requests
 * @param {Array} tips - Current session tips
 * @param {Date} startTime - Session start time
 * @param {Date} endTime - Session end time
 * @returns {Object} Session summary data
 */
export const calculateSessionSummary = (requests = [], tips = [], startTime, endTime) => {
  const totalRequests = requests.length;
  const playedRequests = requests.filter(
    (req) => req.status === 'played' || req.played === true
  ).length;
  const totalEarnings = requests
    .filter((req) => isPaidSongRequestForLedger(req))
    .reduce((sum, req) => sum + (Number(req.amount_paid) || 0), 0);
  const { totalTips } = deriveTipMetrics(requests, tips);
  
  const durationMinutes = startTime && endTime 
    ? Math.round((endTime - startTime) / (1000 * 60))
    : 0;

  return {
    totalRequests,
    playedRequests,
    totalEarnings,
    totalTips,
    durationMinutes
  };
};
